import { z } from 'zod'
import { type LegalSourceCitationBehavior } from '#app/services/legal-ai/shared/source-contract.ts'

/** Stable application name. It may be an alias resolving to a versioned physical collection. */
export const COLOMBIA_COLLECTION = 'colombia_legal_documents'
export const COLOMBIA_INDEX_VERSION = 2
/** Versions retrieval accepts while a re-index replaces older chunks document by document. */
export const COLOMBIA_SUPPORTED_INDEX_VERSIONS = [1, 2] as const

type ColombiaSourceInfo = {
	/** Institution that publishes this collection (shown to users). */
	label: string
	publisher: string
	portal: string
	/** What the publisher's copy proves; consolidations and compilations are not the authentic gazette. */
	authority:
		| 'issuing_court'
		| 'issuing_authority'
		| 'official_compilation'
		| 'official_consolidation'
		| 'official_open_data_inventory'
	/** Letters below which extracted text is treated as an empty shell. Defaults to 400. */
	minimumTextLetters?: number
}

export const COLOMBIA_SOURCES = {
	'corte-constitucional': {
		label: 'Corte Constitucional — Relatoría',
		publisher: 'Corte Constitucional de Colombia',
		portal: 'www.corteconstitucional.gov.co',
		authority: 'issuing_court',
	},
	'consejo-estado': {
		label: 'Consejo de Estado — Relatoría (SAMAI)',
		publisher: 'Consejo de Estado de Colombia',
		portal: 'samai.consejodeestado.gov.co',
		authority: 'issuing_court',
	},
	'gestor-normativo': {
		label: 'Función Pública — Gestor Normativo',
		publisher: 'Departamento Administrativo de la Función Pública',
		portal: 'www.funcionpublica.gov.co',
		authority: 'official_compilation',
	},
	'suin-juriscol': {
		label: 'SUIN-Juriscol — Ministerio de Justicia',
		publisher: 'Ministerio de Justicia y del Derecho',
		portal: 'lexis.minjusticia.gov.co',
		authority: 'official_compilation',
		// Historical appointment decrees are a few lines long but complete.
		minimumTextLetters: 80,
	},
	'bogota-regimen-legal': {
		label: 'Alcaldía de Bogotá — Régimen Legal (SISJUR)',
		publisher: 'Secretaría Jurídica Distrital de Bogotá',
		portal: 'www.alcaldiabogota.gov.co',
		authority: 'official_compilation',
	},
	// Other distritos: acts published by each district's alcaldía, concejo or official gazette.
	'medellin-distrito': {
		label: 'Distrito de Medellín — Gaceta Oficial',
		publisher: 'Alcaldía de Medellín',
		portal: 'www.medellin.gov.co',
		authority: 'issuing_authority',
	},
	'cali-distrito': {
		label: 'Distrito de Santiago de Cali — Boletín Oficial y Normagrama',
		publisher: 'Alcaldía de Santiago de Cali',
		portal: 'www.cali.gov.co',
		authority: 'issuing_authority',
	},
	'cartagena-distrito': {
		label: 'Distrito de Cartagena de Indias — acuerdos del Concejo Distrital',
		publisher: 'Concejo Distrital de Cartagena de Indias',
		portal: 'concejodistritaldecartagena.gov.co',
		authority: 'issuing_authority',
	},
	'barranquilla-distrito': {
		label: 'Distrito de Barranquilla — acuerdos y normas distritales',
		publisher: 'Concejo Distrital de Barranquilla',
		portal: 'www.concejodebarranquilla.gov.co',
		authority: 'issuing_authority',
	},
	'santa-marta-distrito': {
		label: 'Distrito de Santa Marta — Gaceta Distrital y acuerdos del Concejo',
		publisher: 'Alcaldía Distrital de Santa Marta',
		portal: 'www.santamarta.gov.co',
		authority: 'issuing_authority',
	},
	'buenaventura-distrito': {
		label: 'Distrito de Buenaventura — acuerdos, decretos y resoluciones distritales',
		publisher: 'Alcaldía Distrital de Buenaventura',
		portal: 'www.buenaventura.gov.co',
		authority: 'issuing_authority',
	},
	'riohacha-distrito': {
		label: 'Distrito de Riohacha — normas distritales',
		publisher: 'Alcaldía Distrital de Riohacha',
		portal: 'www.riohacha-laguajira.gov.co',
		authority: 'issuing_authority',
	},
	'turbo-distrito': {
		label: 'Distrito de Turbo — normas distritales',
		publisher: 'Alcaldía Distrital de Turbo',
		portal: 'www.turbo-antioquia.gov.co',
		authority: 'issuing_authority',
	},
	'tumaco-distrito': {
		label: 'Distrito de San Andrés de Tumaco — normatividad de la Alcaldía Distrital',
		publisher: 'Alcaldía Distrital de San Andrés de Tumaco',
		portal: 'www.tumaco-narino.gov.co',
		authority: 'issuing_authority',
	},
	'mompox-distrito': {
		label: 'Distrito de Santa Cruz de Mompox — normatividad de la Alcaldía Distrital',
		publisher: 'Alcaldía Distrital de Santa Cruz de Mompox',
		portal: 'www.santacruzdemompos-bolivar.gov.co',
		authority: 'issuing_authority',
	},
	'barrancabermeja-distrito': {
		label: 'Distrito de Barrancabermeja — acuerdos del Concejo Distrital',
		publisher: 'Concejo Distrital de Barrancabermeja',
		portal: 'concejobarrancabermeja.gov.co',
		authority: 'issuing_authority',
	},
} as const satisfies Record<string, ColombiaSourceInfo>

export type ColombiaSourceType = keyof typeof COLOMBIA_SOURCES
export const COLOMBIA_SOURCE_TYPES = Object.keys(COLOMBIA_SOURCES) as [ColombiaSourceType, ...ColombiaSourceType[]]

export const COLOMBIA_LEGAL_KINDS = [
	'constitution',
	'statute',
	'decree_with_force_of_law',
	'regulatory_decree',
	'administrative_act',
	'administrative_concept',
	'circular_or_directive',
	'territorial_act',
	'judgment',
	'judicial_order',
	'treaty',
	'bill_or_history',
	'other',
] as const
export type ColombiaLegalKind = (typeof COLOMBIA_LEGAL_KINDS)[number]

export const colombiaRecordSchema = z.object({
	idDocument: z.string().regex(/^co-[a-z0-9-]+$/),
	country: z.literal('CO'),
	sourceType: z.enum(COLOMBIA_SOURCE_TYPES),
	/** Namespaced citation source, e.g. co/corte-constitucional. */
	sourceId: z.string().startsWith('co/'),
	publisher: z.string(),
	sourcePortal: z.string(),
	/** Body that issued the act or decision (court, ministry, council), when stated by the source. */
	authority: z.string().optional(),
	legalKind: z.enum(COLOMBIA_LEGAL_KINDS),
	documentType: z.string(),
	title: z.string(),
	number: z.string(),
	year: z.number().int().nullable().optional(),
	/** Issuance or decision date stated by the source (ISO). Never derived from crawl or upload time. */
	date: z.string().nullable().optional(),
	/** Publication date (e.g. Diario Oficial) when stated separately. */
	publishedAt: z.string().nullable().optional(),
	originalIdentifier: z.string(),
	/** Normalized identifiers for exact lookup, e.g. ley-1437-2011, t-153-26. */
	citationKeys: z.array(z.string()).default([]),
	url: z.string().url(),
	alternateUrls: z.array(z.string().url()).default([]),
	territorialScope: z
		.object({
			level: z.enum(['national', 'departmental', 'district', 'municipal']),
			locality: z.string().optional(),
		})
		.default({ level: 'national' }),
	/**
	 * full_text: the issuing body's text; consolidated_text: a compilation with
	 * amendment notes; partial_text: excerpt or incomplete extraction;
	 * metadata_only: catalog card without the operative text.
	 */
	textScope: z.enum(['full_text', 'consolidated_text', 'partial_text', 'metadata_only']),
	extraction: z
		.object({
			method: z.enum(['html', 'pdf_text', 'ocr']),
			version: z.number().int(),
			warnings: z.array(z.string()).default([]),
			pages: z.number().int().nullable().optional(),
			annexesComplete: z.boolean().nullable().optional(),
		})
		.optional(),
	legalMetadata: z
		.object({
			/** Vigency/status text exactly as published by the compiler; informational, not certification. */
			vigencyNote: z.string().optional(),
			gazette: z.string().optional(),
			subjects: z.array(z.string()).optional(),
			issuer: z.string().optional(),
		})
		.optional(),
	caseMetadata: z
		.object({
			court: z.string().optional(),
			chamber: z.string().optional(),
			decisionType: z.string().optional(),
			process: z.string().optional(),
			docket: z.array(z.string()).optional(),
			reportingJudges: z.array(z.string()).optional(),
			separateOpinions: z.array(z.string()).optional(),
			/** Official publisher flags only (e.g. SAMAI RelevanteUnificacion); never inferred. */
			officialFlags: z.array(z.string()).optional(),
			legalProblem: z.string().optional(),
			thesis: z.string().optional(),
		})
		.optional(),
	retrievedAt: z.string().optional(),
})

export type ColombiaRecord = z.infer<typeof colombiaRecordSchema>
export type ColombiaChunkArticles = {
	/** Articles whose heading starts inside this chunk (normalized, e.g. "64", "13a", "unico"). */
	articles?: string[]
	/** Article the chunk continues when it starts inside an article's text. */
	continuesArticle?: string | null
}

export type ColombiaPayload = ColombiaRecord &
	ColombiaChunkArticles & {
	content: string
	chunk_index: number
	chunk_count: number
	index_version: number
	content_hash: string
}

export function getColombiaSourceInfo(sourceType: string) {
	return (COLOMBIA_SOURCES as Record<string, ColombiaSourceInfo>)[sourceType] ?? null
}

const citation: LegalSourceCitationBehavior<any> = {
	title: payload =>
		[payload.documentType && payload.number ? `${payload.documentType} ${payload.number}` : null, payload.title]
			.filter(Boolean)
			.join(' — '),
	url: payload => payload.url || '',
	inlineTagName: 'Colombia',
	iconName: 'file-text',
	inlineSecondaryText: payload =>
		[payload.authority || payload.caseMetadata?.court, payload.date?.slice(0, 10)].filter(Boolean).join(' · ') || null,
}

export const colombiaSourceDefinitions = COLOMBIA_SOURCE_TYPES.map(sourceType => {
	const info = COLOMBIA_SOURCES[sourceType]
	return {
		id: `co/${sourceType}`,
		country: 'CO' as const,
		sourceType,
		label: info.label,
		citationType: `Colombia-${sourceType}`,
		citation: { ...citation, inlineTagName: `colombia-${sourceType}`, summaryLabel: info.label },
	}
})

export function colombiaCitationTag(record: Pick<ColombiaRecord, 'sourceType' | 'idDocument'>) {
	return `<document type="Colombia-${record.sourceType}" id="${record.idDocument}" />`
}

function serialize(value: unknown, maxLength: number) {
	if (value === undefined || value === null) return ''
	const text = typeof value === 'string' ? value : JSON.stringify(value)
	return text.length <= maxLength ? text : `${text.slice(0, maxLength)}…`
}

const textScopeLabels: Record<ColombiaRecord['textScope'], string> = {
	full_text: 'texto íntegro publicado por la fuente',
	consolidated_text: 'texto compilado con notas de vigencia (informativo; no sustituye el Diario Oficial)',
	partial_text: 'texto parcial o extracción incompleta',
	metadata_only: 'ficha de catálogo sin articulado',
}

const ORDINALS: Record<string, string> = {
	primero: '1', segundo: '2', tercero: '3', cuarto: '4', quinto: '5', sexto: '6', septimo: '7', octavo: '8', noveno: '9', decimo: '10',
}
// Case-sensitive on purpose: an uppercase suffix is part of the number (49A),
// while "1o." is the ordinal of article 1.
const ARTICLE_HEADING =
	/(?:^|\n)[ \t]*(?:ART[IÍ]CULO|Art[ií]culo)[ \t]+(\d+(?:[ \t]?[A-Z](?![a-zA-Z]))?|[ÚU]NICO|[Úú]nico|PRIMERO|[Pp]rimero|SEGUNDO|[Ss]egundo|TERCERO|[Tt]ercero|CUARTO|[Cc]uarto|QUINTO|[Qq]uinto|SEXTO|[Ss]exto|S[EÉ]PTIMO|[Ss][eé]ptimo|OCTAVO|[Oo]ctavo|NOVENO|[Nn]oveno|D[EÉ]CIMO|[Dd][eé]cimo)/gu

function normalizeArticle(value: string) {
	const folded = value
		.normalize('NFD')
		.replace(/\p{Diacritic}/gu, '')
		.replace(/\s+/g, '')
		.toLowerCase()
	return ORDINALS[folded] ?? folded
}

/** Article headings with their offsets, in document order. */
export function findColombiaArticleHeadings(text: string) {
	return [...text.matchAll(ARTICLE_HEADING)].map(match => ({
		index: match.index! + (match[0].startsWith('\n') ? 1 : 0),
		article: normalizeArticle(match[1]!),
	}))
}

/**
 * Article context for each chunk: headings that start inside it, and the
 * article it continues when it begins inside an article. Chunks are substrings
 * of the normalized text, located in order.
 */
export function colombiaChunkArticles(text: string, chunks: string[]): ColombiaChunkArticles[] {
	const headings = findColombiaArticleHeadings(text)
	let cursor = 0
	return chunks.map(chunk => {
		const probe = chunk.slice(0, 200)
		const found = text.indexOf(probe, Math.max(0, cursor - chunk.length))
		const start = found >= 0 ? found : cursor
		const end = start + chunk.length
		cursor = found >= 0 ? found + 1 : cursor
		const inside = [...new Set(headings.filter(heading => heading.index >= start && heading.index < end).map(heading => heading.article))]
		const before = headings.filter(heading => heading.index < start).at(-1)
		const startsWithHeading = headings.some(heading => heading.index === start)
		return { articles: inside, continuesArticle: !startsWithHeading && before ? before.article : null }
	})
}

/** Metadata header shared by embeddings and the model context. */
export function buildColombiaEmbeddingText(record: ColombiaRecord & ColombiaChunkArticles, content: string) {
	const info = getColombiaSourceInfo(record.sourceType)
	const territorial =
		record.territorialScope.level === 'national'
			? ''
			: `Ámbito territorial: ${record.territorialScope.level}${record.territorialScope.locality ? ` — ${record.territorialScope.locality}` : ''}`
	return [
		'País: Colombia',
		`Fuente: ${info?.label ?? record.sourceType} (${record.publisher})`,
		`Tipo: ${record.documentType}`,
		record.number ? `Número: ${record.number}` : '',
		`Título: ${record.title}`,
		record.authority ? `Autoridad emisora: ${record.authority}` : '',
		record.caseMetadata?.chamber ? `Sala/Sección: ${record.caseMetadata.chamber}` : '',
		record.caseMetadata?.reportingJudges?.length ? `Ponente: ${record.caseMetadata.reportingJudges.join(', ')}` : '',
		record.caseMetadata?.docket?.length ? `Radicación/expediente: ${record.caseMetadata.docket.join(', ')}` : '',
		record.caseMetadata?.officialFlags?.length
			? `Marcas oficiales del publicador: ${record.caseMetadata.officialFlags.join(', ')}`
			: '',
		record.date ? `Fecha del documento: ${record.date.slice(0, 10)}` : 'Fecha del documento: no indicada por la fuente',
		record.publishedAt ? `Fecha de publicación: ${record.publishedAt.slice(0, 10)}` : '',
		record.legalMetadata?.gazette ? `Publicación oficial: ${record.legalMetadata.gazette}` : '',
		territorial,
		`Alcance del texto: ${textScopeLabels[record.textScope]}`,
		record.continuesArticle ? `Continúa el artículo ${record.continuesArticle}` : '',
		record.articles?.length ? `Artículos que inician en este fragmento: ${record.articles.slice(0, 40).join(', ')}` : '',
		record.legalMetadata?.vigencyNote
			? `Nota de vigencia del compilador (informativa): ${serialize(record.legalMetadata.vigencyNote, 2_000)}`
			: '',
		record.legalMetadata?.subjects?.length ? `Materias: ${serialize(record.legalMetadata.subjects, 1_000)}` : '',
		record.caseMetadata?.legalProblem ? `Problema jurídico (relatoría): ${serialize(record.caseMetadata.legalProblem, 2_000)}` : '',
		record.extraction?.method === 'ocr' ? 'Extracción: OCR (puede contener errores de reconocimiento)' : '',
		content,
	]
		.filter(Boolean)
		.join('\n')
}

export function buildColombiaCitationGroundingText(record: ColombiaRecord, content: string) {
	// A catalog card proves identity and recorded metadata only; keep that framing with the citation.
	return record.textScope === 'metadata_only' ? buildColombiaEmbeddingText(record, content) : content
}

/**
 * Reject navigation shells, error pages and summaries masquerading as documents.
 * A real Colombian norm or decision contains sustained prose; portals' empty
 * templates contain mostly menus, which this conservative check rejects.
 */
export function minimumColombiaTextLetters(sourceType: string) {
	return getColombiaSourceInfo(sourceType)?.minimumTextLetters ?? 400
}

export function hasSubstantiveColombiaText(text: unknown, minimumLetters = 400): text is string {
	if (typeof text !== 'string') return false
	const letters = (text.match(/\p{L}/gu) ?? []).length
	if (letters < minimumLetters) return false
	if (/The requested URL was rejected|Request Rejected|Access Denied|captcha/i.test(text.slice(0, 2_000))) return false
	const replacement = (text.match(/�/g) ?? []).length
	return replacement / Math.max(1, text.length) < 0.01
}
